import { useDeviceManagerState } from 'app/containers/rtc/DeviceManagerContext';
import TrackState from 'app/services/rtc/TrackState';
import React, { FunctionComponent, memo, useContext, useEffect, useState } from 'react';
import VolumeAnalyser, { VolumeAnalyserEventHandlerMap } from '../../services/rtc/VolumeAnalyser';

const VolumeContext = React.createContext<number>(0);
const SpeakingContext = React.createContext<boolean>(false);

export const useVolume = () => useContext(VolumeContext);
export const useIsSpeaking = () => useContext(SpeakingContext);

export const IsSpeaking = SpeakingContext.Consumer;

interface IVolumeAnalyserProviderProps {
  readonly initialThreshold?: number;
}

const VolumeAnalyserProvider: FunctionComponent<IVolumeAnalyserProviderProps> = memo(({
  children,
  initialThreshold,
}) => {
  const [speaking, setSpeaking] = useState(false);
  const [volume, setVolume] = useState(0);
  const { audio } = useDeviceManagerState();

  const [volumeAnalyser] = useState(() => new VolumeAnalyser({
    ...typeof initialThreshold !== 'undefined' && { threshold: initialThreshold },
    ...typeof audio.analyser !== 'undefined' && { analyser: audio.analyser },
    resolution: 5,
    probeTime: 100,
  }));

  useEffect(() => {
    const eventMap: VolumeAnalyserEventHandlerMap = {
      speaking: (value: boolean) => setSpeaking(value),
      volume:   (value: number) => setVolume(value),
    };
    volumeAnalyser.bindEvents(eventMap);

    return () => {
      volumeAnalyser.unbindEvents(eventMap);
    };
  }, [volumeAnalyser]);

  // The motivation for removing the analyser when the input audio is inactive, is so we can stop the
  // unnecessary analysis. This is especially needed for dead silence analysis. Let's say we disabled
  // the microphone, we don't want the analyser to keep on checking if there's any signal coming from the
  // input audio, causing it to initially mark it as dead silence/no signal when the user enables back
  // the microphone.
  const desiredAnalyser = audio.trackState !== TrackState.Active ? undefined : audio.analyser;

  useEffect(() => {
    volumeAnalyser.setAnalyser(desiredAnalyser);

    return () => {
      volumeAnalyser.setAnalyser(undefined);
    };
  }, [volumeAnalyser, desiredAnalyser]);

  return (
    <SpeakingContext.Provider value={speaking}>
      <VolumeContext.Provider value={volume}>
        {children}
      </VolumeContext.Provider>
    </SpeakingContext.Provider>
  );
});

export default VolumeAnalyserProvider;
